import React, { useEffect, useRef, useState } from 'react';
import { talkUrl } from '@/config/hosts';

const BARS = 92;
const LINES = [
  'आईला chemotherapy चालू आहे, तिला काय खायला द्यायचं?',
  'Her platelets are 40,000. Should we be worried?',
  'தந்தைக்கு radiation ஆரம்பம், பக்க விளைவுகள் என்ன?',
  'Transplant के बाद बुखार आए तो क्या करें?',
  '¿Puedo viajar entre ciclos de quimioterapia?',
  'ಇದಕ್ಕೆ clinical trial ಇದೆಯಾ?',
];

/**
 * Marketing preview of the Vani ring. Same 92-bar face as the app.
 * Cancels rAF and timers on unmount so Jest does not leak handles.
 */
export default function HeroRing() {
  const canvasRef = useRef(null);
  const [caption, setCaption] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return undefined;

    const level = new Float32Array(BARS);
    const seed = Array.from({ length: BARS }, () => Math.random() * Math.PI * 2);
    let phase = Math.random() * 99;
    let energy = 0;
    let target = 0;
    let raf = 0;
    let size = { w: 0, h: 0 };
    let cancelled = false;

    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      size = { w: r.width, h: r.height };
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(canvas);

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const draw = () => {
      if (cancelled) return;
      const { w, h } = size;
      if (w) {
        ctx.clearRect(0, 0, w, h);
        const cx = w / 2;
        const cy = h / 2;
        const S = Math.min(w, h);
        const r0 = S * 0.33;
        const maxLen = S * 0.128;
        phase += 0.045;
        energy += (target - energy) * 0.06;
        for (let i = 0; i < BARS; i += 1) {
          const s = Math.sin(phase * 1.5 + seed[i]) * Math.sin(phase * 0.61 + seed[i] * 1.7);
          const t = 0.08 + Math.abs(s) * 0.8 * energy + (Math.sin(phase * 0.5 - i * 0.14) + 1) / 2 * 0.09;
          level[i] += (t - level[i]) * (t > level[i] ? 0.4 : 0.13);
          const v = Math.min(level[i], 1.2);
          const a = (i / BARS) * Math.PI * 2 - Math.PI / 2;
          const len = maxLen * v;
          ctx.strokeStyle = `hsl(${250 - (i / BARS) * 88} 92% ${62 - Math.min(v, 1) * 20}%)`;
          ctx.lineWidth = Math.max(2, S * 0.0092);
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
          ctx.lineTo(cx + Math.cos(a) * (r0 + len), cy + Math.sin(a) * (r0 + len));
          ctx.stroke();
        }
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    draw();

    let li = 0;
    let ci = 0;
    let del = false;
    let typeTimer = 0;
    const type = () => {
      if (cancelled) return;
      const line = LINES[li];
      ci += del ? -1 : 1;
      const next = line.slice(0, ci);
      setCaption(next);
      target = del ? 0.15 : 0.85;
      if (!del && ci === line.length) {
        del = true;
        target = 0.15;
        typeTimer = window.setTimeout(type, 2100);
        return;
      }
      if (del && ci === 0) {
        del = false;
        li = (li + 1) % LINES.length;
        typeTimer = window.setTimeout(type, 320);
        return;
      }
      typeTimer = window.setTimeout(type, del ? 18 : 46 + Math.random() * 40);
    };
    if (!reduced) typeTimer = window.setTimeout(type, 400);
    else setCaption(LINES[0]);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(typeTimer);
      if (ro) ro.disconnect();
    };
  }, []);

  return (
    <a href={talkUrl('/')} className="vani-ringwrap" aria-label="Talk to Vani">
      <canvas ref={canvasRef} />
      <div className="cap deva" aria-hidden="true">
        “<span className="typed">{caption}</span>”
      </div>
    </a>
  );
}
